import React from 'react';
import Markdown from 'react-markdown';
import { Link, useParams } from 'react-router-dom';
import CategoryBadge from '../components/CategoryBadge';
import Seo from '../components/Seo';
import ShareButtons from '../components/ShareButtons';
import ImageWithFallback from '../components/ImageWithFallback';
import {
  getAllPosts,
  getPostBySlug,
  getPrevNextPosts,
  getRelatedPosts
} from '../content/config';

const flattenText = (node) => {
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(flattenText).join('');
  if (node && typeof node === 'object' && 'props' in node) return flattenText(node.props?.children);
  return '';
};

const toHeadingId = (value) =>
  flattenText(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

const blocks = [
  'Ce que ca change pour vous',
  "L'info que les gens n'ont pas vue",
  'A faire maintenant',
  'Sources'
];

const ArticleView = () => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  if (!post) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center">
        <Seo title="Article introuvable" path={`/article/${slug}`} />
        <h1 className="lmd-editorial-title text-4xl font-bold text-slate-900">Article introuvable</h1>
        <Link to="/" className="mt-6 inline-flex rounded-md bg-indigo-600 px-4 py-2 text-white">
          Retour accueil
        </Link>
      </div>
    );
  }

  const headings = post.headings
    .filter((heading) => heading && heading.trim().length > 0)
    .slice(0, 12);
  const related = getRelatedPosts(post, 6).slice(0, 4);
  const { prev, next } = getPrevNextPosts(post.slug);
  const allPosts = getAllPosts();

  return (
    <article className="mx-auto max-w-[1200px] px-4 py-10">
      <Seo
        title={post.title}
        description={post.summary}
        path={`/article/${post.slug}`}
        image={post.cover}
        type="article"
      />

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
        <aside className="lg:col-span-3">
          <div className="lg:sticky lg:top-24 rounded-xl border border-slate-200 bg-white p-4">
            <h2 className="text-xs font-black uppercase tracking-widest text-slate-800">Sommaire</h2>
            <ul className="mt-3 space-y-2 text-sm">
              {headings.map((heading) => {
                const id = toHeadingId(heading);
                return (
                  <li key={id}>
                    <a href={`#${id}`} className="text-slate-600 hover:text-indigo-600">
                      {heading}
                    </a>
                  </li>
                );
              })}
            </ul>
            <div className="mt-5 border-t border-slate-200 pt-4">
              <p className="text-xs uppercase tracking-wider text-slate-500">A lire aussi</p>
              <p className="mt-1 text-sm text-slate-600">{Math.min(related.length, 4)} article(s) lies</p>
            </div>
            <div className="mt-4 hidden lg:block">
              <ShareButtons variant="compactSticky" />
            </div>
          </div>
        </aside>

        <div className="lg:col-span-9">
          <header className="mb-8">
            <div className="mb-3 flex items-center gap-3">
              <CategoryBadge category={post.category} />
              <span className="text-xs uppercase tracking-wider text-slate-400">{post.readingTime}</span>
              <span className="text-xs uppercase tracking-wider text-slate-400">{post.date}</span>
            </div>
            <h1 className="lmd-editorial-title text-4xl font-bold leading-tight text-slate-900 md:text-5xl">{post.title}</h1>
            <p className="mt-4 text-lg text-slate-600">{post.summary}</p>
            <div className="lg:hidden">
              <ShareButtons />
            </div>
          </header>

          <ImageWithFallback
            src={post.cover}
            alt={post.title}
            categorySlug={post.category}
            className="mb-8 h-[360px] w-full rounded-xl object-cover"
          />

          <div className="prose prose-slate max-w-none">
            <Markdown
              components={{
                h2: ({ node, ...props }) => {
                  const id = toHeadingId(props.children);
                  return <h2 id={id} className="lmd-editorial-title mt-10 text-3xl" {...props} />;
                },
                h3: ({ node, ...props }) => {
                  const id = toHeadingId(props.children);
                  return <h3 id={id} className="mt-6 text-xl font-semibold text-slate-900" {...props} />;
                }
              }}
            >
              {post.content}
            </Markdown>
          </div>

          <section className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-2">
            {blocks.map((block) => (
              <div key={block} className="rounded-xl border border-slate-200 bg-white p-4">
                <h3 className="text-sm font-black uppercase tracking-wider text-slate-800">{block}</h3>
                <p className="mt-2 text-sm text-slate-500">Bloc editorial pret a etre enrichi.</p>
              </div>
            ))}
          </section>

          <section className="mt-12">
            <h2 className="text-sm font-black uppercase tracking-wider text-slate-800">A lire aussi</h2>
            <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2">
              {related.map((item) => (
                <Link key={item.slug} to={`/article/${item.slug}`} className="rounded-xl border border-slate-200 bg-white p-4">
                  <p className="text-xs uppercase tracking-wider text-slate-500">{item.categoryLabel}</p>
                  <h3 className="mt-1 text-lg font-bold text-slate-900">{item.title}</h3>
                </Link>
              ))}
            </div>
          </section>

          <section className="mt-10 flex flex-col gap-4 border-t border-slate-200 pt-6 md:flex-row md:justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-500">Article precedent</p>
              {prev ? (
                <Link className="text-slate-900 hover:text-indigo-600" to={`/article/${prev.slug}`}>
                  {prev.title}
                </Link>
              ) : (
                <p className="text-slate-400">Aucun</p>
              )}
            </div>
            <div className="md:text-right">
              <p className="text-xs uppercase tracking-wider text-slate-500">Article suivant</p>
              {next ? (
                <Link className="text-slate-900 hover:text-indigo-600" to={`/article/${next.slug}`}>
                  {next.title}
                </Link>
              ) : (
                <p className="text-slate-400">Aucun</p>
              )}
            </div>
          </section>

          <section className="mt-8">
            <h2 className="text-sm font-black uppercase tracking-wider text-slate-800">Tags</h2>
            <div className="mt-2 flex flex-wrap gap-2">
              {post.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/tags/${tag}`}
                  className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600 hover:border-indigo-300 hover:text-indigo-600"
                >
                  #{tag}
                </Link>
              ))}
            </div>
            <p className="mt-4 text-xs text-slate-400">Catalogue total: {allPosts.length} articles.</p>
          </section>
        </div>
      </div>
    </article>
  );
};

export default ArticleView;